import React, { useState } from "react";
import {
  Library, Search, Plus, BookOpen, ExternalLink, Trash2, Tag, Loader2, GraduationCap, Settings, Link2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useTheme } from "@/theme";

const TYPE_ICONS: Record<string, string> = {
  book: "📚",
  journal: "📰",
  article: "📄",
  website: "🌐",
  thesis: "🎓",
  other: "📎",
};

const SCHOLARLY_RESOURCE_SECTIONS = [
  {
    title: "Key Databases & Search Engines",
    icon: "🔎",
    links: [
      { label: "Google Scholar", url: "https://scholar.google.com" },
      { label: "JSTOR", url: "https://www.jstor.org" },
      { label: "PubMed", url: "https://pubmed.ncbi.nlm.nih.gov" },
      { label: "Scopus", url: "https://www.scopus.com" },
      { label: "Web of Science", url: "https://www.webofscience.com" },
      { label: "Science.gov", url: "https://www.science.gov" },
    ],
  },
  {
    title: "Reference & Citation Tools",
    icon: "🧾",
    links: [
      { label: "Zotero", url: "https://www.zotero.org" },
      { label: "Mendeley", url: "https://www.mendeley.com" },
      { label: "EndNote", url: "https://endnote.com" },
      { label: "Petal", url: "https://petal.dev" },
    ],
  },
  {
    title: "Publishers & Repositories",
    icon: "🏛️",
    links: [
      { label: "Oxford University Press", url: "https://academic.oup.com" },
      { label: "Cambridge University Press", url: "https://www.cambridge.org" },
      { label: "University of California Press", url: "https://www.ucpress.edu" },
      { label: "Elsevier", url: "https://www.elsevier.com" },
      { label: "Springer", url: "https://link.springer.com" },
      { label: "Wiley", url: "https://onlinelibrary.wiley.com" },
      { label: "Taylor & Francis", url: "https://www.tandfonline.com" },
      { label: "HathiTrust", url: "https://www.hathitrust.org" },
    ],
  },
];

const ESSENTIAL_FORMATS = [
  "Peer-Reviewed Journal Articles",
  "Academic Books & Monographs",
  "Conference Proceedings",
  "Dissertations & Theses",
];

const SOURCE_TIPS = [
  "Confirm the source type (scholarly vs. popular).",
  "Check author credentials and institutional affiliation.",
  "Verify the publisher (university press or reputable imprint).",
  "Use advanced filters for peer-review and open access.",
  "Capture citation metadata immediately to avoid rework.",
];

type Source = {
  id: string;
  title: string;
  authors: string;
  year: string;
  url: string;
  publisher: string;
  source_type: string;
  tags: string[];
  notes: string;
};

export default function LibraryPanel({ documentId, onAddToDocument }: { documentId?: number; onAddToDocument?: (source: any) => void }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [schoolLibraryOpen, setSchoolLibraryOpen] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [schoolLibraryUrl, setSchoolLibraryUrl] = useState("");
  const { isDark } = useTheme();

  // Theme-aware styles
  const textPrimary = "text-white";
  const textSecondary = isDark ? "text-slate-400" : "text-gray-700";
  const textMuted = isDark ? "text-slate-400" : "text-gray-600";
  const borderColor = isDark ? "border-white/5" : "border-slate-300";
  const cardBg = isDark ? "bg-white/5 border-white/10" : "bg-white border-slate-200";

  // Local sources state (replace with useEntity for persistent storage)
  const [sources, setSources] = useState<Source[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [newSource, setNewSource] = useState<Source>({
    id: "",
    title: "",
    authors: "",
    year: "",
    url: "",
    publisher: "",
    source_type: "article",
    tags: [],
    notes: ""
  });
  const [tagInput, setTagInput] = useState("");

  const resetForm = () => {
    setNewSource({
      id: "",
      title: "",
      authors: "",
      year: "",
      url: "",
      publisher: "",
      source_type: "article",
      tags: [],
      notes: ""
    });
    setTagInput("");
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !newSource.tags.includes(tagInput.trim())) {
      setNewSource(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setNewSource(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleAddSource = () => {
    if (!newSource.title) return;
    setSources(prev => [
      ...prev,
      { ...newSource, id: Date.now().toString() }
    ]);
    setAddDialogOpen(false);
    resetForm();
  };

  const handleDeleteSource = (id: string) => {
    setSources(prev => prev.filter(s => s.id !== id));
  };

  const handleAddToDocument = (source: Source) => {
    if (onAddToDocument) {
      onAddToDocument(source);
    }
  };

  // Get all unique tags
  const allTags = [...new Set(sources.flatMap(s => s.tags || []))];

  // Filter sources
  const filteredSources = sources.filter(source => {
    const matchesSearch =
      source.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.authors?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = !filterTag || (source.tags || []).includes(filterTag);
    return matchesSearch && matchesTag;
  });

  return (
    <div className="h-full flex flex-col text-white">
      <div className={`p-4 border-b ${borderColor}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Library className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold text-white">My Library</h3>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSchoolLibraryOpen(true)}
            className="h-7 w-7 p-0 text-slate-400 hover:text-white"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
        <p className={`text-xs ${textMuted} mb-3`}>
          Your saved sources across all documents
        </p>

        {/* School Library Link */}
        {schoolLibraryUrl && (
          <a
            href={schoolLibraryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 mb-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20"
          >
            <GraduationCap className="w-4 h-4 text-emerald-400" />
            <span className="text-sm flex-1 truncate text-emerald-300">
              {schoolName || "My School"} Library
            </span>
            <ExternalLink className="w-3 h-3 text-emerald-400" />
          </a>
        )}

        <div className="space-y-2 mb-3">
          {SCHOLARLY_RESOURCE_SECTIONS.map((section) => (
            <div key={section.title} className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs font-medium mb-2 text-blue-300 flex items-center gap-1">
                <span>{section.icon}</span>
                {section.title}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {section.links.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] px-2 py-1 rounded-full border border-blue-400/30 text-blue-200 hover:bg-blue-500/20"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-2 mb-3">
          <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <p className="text-xs font-semibold text-purple-200 mb-2">Essential Scholarly Formats</p>
            <ul className="text-[11px] text-purple-100 space-y-1">
              {ESSENTIAL_FORMATS.map((format) => (
                <li key={format} className="flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-purple-300" />
                  {format}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-xs font-semibold text-emerald-200 mb-2">How to Vet a Source</p>
            <ul className="text-[11px] text-emerald-100 space-y-1">
              {SOURCE_TIPS.map((tip) => (
                <li key={tip} className="flex items-start gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 mt-1" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex gap-2 mb-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search library..."
              className="pl-8 bg-white/5 border-white/10 text-white text-sm h-8"
            />
          </div>
          <Button
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            className="btn-gradient text-white h-8"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <Badge
              variant="outline"
              className={`cursor-pointer text-xs ${!filterTag ? 'bg-purple-500/20 text-purple-300' : 'text-slate-400'}`}
              onClick={() => setFilterTag("")}
            >
              All
            </Badge>
            {allTags.slice(0, 5).map(tag => (
              <Badge
                key={tag}
                variant="outline"
                className={`cursor-pointer text-xs ${filterTag === tag ? 'bg-purple-500/20 text-purple-300' : 'text-slate-400'}`}
                onClick={() => setFilterTag(tag)}
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="text-center py-8">
            <Library className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              {sources.length === 0
                ? "Your library is empty. Add sources to reuse across documents."
                : "No sources match your search."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSources.map((source) => (
              <div
                key={source.id}
                className={`p-3 rounded-lg border group ${cardBg}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-lg">{TYPE_ICONS[source.source_type] || TYPE_ICONS.other}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{source.title}</p>
                    <p className="text-xs text-slate-400">
                      {source.authors} {source.year && `(${source.year})`}
                    </p>
                    {source.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {source.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="text-[10px] text-slate-500 border-slate-600">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {documentId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                      onClick={() => handleAddToDocument(source)}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add to Doc
                    </Button>
                  )}
                  {source.url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-blue-400 hover:text-blue-300"
                      onClick={() => window.open(source.url, '_blank')}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    onClick={() => handleDeleteSource(source.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* School Library Settings Dialog */}
      <Dialog open={schoolLibraryOpen} onOpenChange={setSchoolLibraryOpen}>
        <DialogContent className="max-w-sm bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-emerald-400" />
              School Library
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">School Name</label>
              <Input
                placeholder="e.g., Harvard University"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Library URL</label>
              <Input
                placeholder="https://library.university.edu"
                value={schoolLibraryUrl}
                onChange={(e) => setSchoolLibraryUrl(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                This link will appear in your library for quick access
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setSchoolLibraryOpen(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button onClick={() => setSchoolLibraryOpen(false)} className="btn-gradient text-white">
              <Link2 className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Source Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add to Library</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              placeholder="Title *"
              value={newSource.title}
              onChange={(e) => setNewSource(prev => ({ ...prev, title: e.target.value }))}
              className="bg-white/5 border-white/10 text-white"
            />
            <Input
              placeholder="Authors"
              value={newSource.authors}
              onChange={(e) => setNewSource(prev => ({ ...prev, authors: e.target.value }))}
              className="bg-white/5 border-white/10 text-white"
            />
            <div className="flex gap-2">
              <Input
                placeholder="Year"
                value={newSource.year}
                onChange={(e) => setNewSource(prev => ({ ...prev, year: e.target.value }))}
                className="bg-white/5 border-white/10 text-white w-24"
              />
              <Select
                value={newSource.source_type}
                onValueChange={(v) => setNewSource(prev => ({ ...prev, source_type: v }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="article">Article</SelectItem>
                  <SelectItem value="book">Book</SelectItem>
                  <SelectItem value="journal">Journal</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="thesis">Thesis</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="URL or DOI"
              value={newSource.url}
              onChange={(e) => setNewSource(prev => ({ ...prev, url: e.target.value }))}
              className="bg-white/5 border-white/10 text-white"
            />
            <Input
              placeholder="Publisher / Journal"
              value={newSource.publisher}
              onChange={(e) => setNewSource(prev => ({ ...prev, publisher: e.target.value }))}
              className="bg-white/5 border-white/10 text-white"
            />

            <div>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  className="bg-white/5 border-white/10 text-white"
                />
                <Button size="sm" variant="ghost" onClick={handleAddTag} className="text-slate-400">
                  <Tag className="w-4 h-4" />
                </Button>
              </div>
              {newSource.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {newSource.tags.map(tag => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-xs text-purple-300 cursor-pointer"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <Textarea
              placeholder="Notes..."
              value={newSource.notes}
              onChange={(e) => setNewSource(prev => ({ ...prev, notes: e.target.value }))}
              className="bg-white/5 border-white/10 text-white h-20"
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddDialogOpen(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button
              onClick={handleAddSource}
              disabled={!newSource.title}
              className="btn-gradient text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Library
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}